/**
 * ASO-ATTR-001 — Apple Ads (AdServices) attribution, device side.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT THIS DOES, IN ONE PARAGRAPH
 *
 * On an iOS binary that contains the AdServices native module, once per install: mint an Apple
 * attribution token and hand it to the Atlas backend, which redeems it against Apple and stores the
 * result. Nothing is redeemed here and no attribution payload is ever readable on the device.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE FOUR RULES THIS MODULE EXISTS TO HOLD
 *
 * 1. A FRESH TOKEN PER ATTEMPT, AND THE TOKEN IS NEVER PERSISTED.
 *    Apple's token has a 24-hour TTL and redeeming an expired one is a 404 that looks exactly like
 *    "this install has no attribution". Saving a token for later retry is therefore the one design
 *    that can silently convert a real attribution into an organic install. Minting is cheap and
 *    always current, so every attempt mints its own, milliseconds before the POST — which is also
 *    what makes "sent within Apple's validity window" true by construction rather than by a timer.
 *    It also means the token never sits in AsyncStorage, where it would outlive its usefulness in
 *    every device backup.
 *
 * 2. EXACTLY ONCE PER INSTALL, ACROSS PROCESSES.
 *    The latch is persisted (`ATTRIBUTION_STORAGE_KEY`) and keyed BY INSTALL ID, so a reinstall
 *    legitimately starts over and an id that changed under us is not mistaken for a completed one.
 *    The backend is idempotent on the same key regardless, because a device latch cannot be trusted
 *    with a de-duplication guarantee — it can be cleared by the OS at any time.
 *
 * 3. A REFUSAL AND AN OUTAGE ARE DIFFERENT THINGS.
 *    A 4xx means this token/install will never be accepted; retrying it forever is a battery and
 *    log cost that buys nothing, so it is terminal. A timeout, a 5xx or a dead network is an
 *    outage: it stays `pending` and is retried on the next launch or foreground, bounded by
 *    `MAX_ATTEMPTS` and `MAX_ATTEMPT_AGE_MS` so a permanently broken backend cannot produce an
 *    unbounded retry loop on a customer's till.
 *
 * 4. IT CANNOT AFFECT THE APP.
 *    Same rule as utils/analyticsIdentity.js, learned from ATL-901: one unguarded telemetry
 *    dereference locked a paying customer out of their till for four hours. Every exported function
 *    resolves; none of them throws into the launch path.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * PRIVACY
 *
 * The device sends exactly three things: the install id, the Apple token, and the moment it was
 * minted. No Firebase UID, no organization, no email, no device identifier, no IDFA — and no ATT
 * prompt is involved at any point (AdServices does not need one; ATT only decides whether Apple's
 * own payload is the standard or the detailed shape). The install→organization association is made
 * later, from an AUTHENTICATED call, and it is the only place the two ever meet.
 */
import { Platform } from 'react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'

import { getAttributionTokenAsync, isAvailable } from '../modules/atlas-adservices'

export const ATTRIBUTION_STORAGE_KEY = 'atlas.appleAdsAttribution.v1'

/** Attempts, in total, across every process, before the install is abandoned. */
export const MAX_ATTEMPTS = 8
/** Wall-clock budget from the first attempt. A week of launches is far more than enough. */
export const MAX_ATTEMPT_AGE_MS = 7 * 24 * 60 * 60 * 1000
/** Minimum spacing between attempts, so a foregrounding loop cannot become a request loop. */
export const MIN_RETRY_INTERVAL_MS = 5 * 60 * 1000
/** The POST is a launch-path side quest. It must not hold a socket open indefinitely. */
export const REQUEST_TIMEOUT_MS = 15000

/** Statuses from which no further attempt will ever be made. */
export const TERMINAL_STATUSES = Object.freeze(['sent', 'refused', 'abandoned'])

/**
 * Decide whether to attempt attribution now — the whole policy, as a pure function.
 *
 * @param {{
 *   record: {installId?: string, status?: string, attempts?: number, firstAttemptAt?: number,
 *            lastAttemptAt?: number} | null | undefined,
 *   installId: string,
 *   now: number,
 *   platform: string,
 *   nativeAvailable: boolean,
 * }} input
 * @returns {{attempt: boolean, reason: string, record: object}} `record` is the state to persist
 *   when `attempt` is false and the reason is a terminal one; otherwise the state to start from.
 */
export function decideAttributionAttempt({ record, installId, now, platform, nativeAvailable }) {
  // Apple Ads attribution exists only on Apple platforms. Android/web are not a failure to report.
  if (platform !== 'ios') {
    return { attempt: false, reason: 'platform-not-ios', record: record || null }
  }
  // An iOS binary built before this module shipped. NOT terminal and NOT persisted as a status:
  // the very next native release makes the same install eligible, and a persisted `unsupported`
  // would be a latch that outlives the reason for it.
  if (!nativeAvailable) {
    return { attempt: false, reason: 'native-module-absent', record: record || null }
  }

  // A record for a different install id is not this install's record. Treat it as absent rather
  // than as a completed attempt — the alternative silently drops attribution for the new install.
  const current =
    record && record.installId === installId
      ? record
      : { installId, status: 'pending', attempts: 0 }

  if (TERMINAL_STATUSES.includes(current.status)) {
    return { attempt: false, reason: `already-${current.status}`, record: current }
  }

  const attempts = Number.isFinite(current.attempts) ? current.attempts : 0
  if (attempts >= MAX_ATTEMPTS) {
    return {
      attempt: false,
      reason: 'attempts-exhausted',
      record: { ...current, status: 'abandoned', abandonedAt: now },
    }
  }

  const firstAttemptAt = Number.isFinite(current.firstAttemptAt) ? current.firstAttemptAt : null
  if (firstAttemptAt != null && now - firstAttemptAt > MAX_ATTEMPT_AGE_MS) {
    return {
      attempt: false,
      reason: 'attempt-window-expired',
      record: { ...current, status: 'abandoned', abandonedAt: now },
    }
  }

  const lastAttemptAt = Number.isFinite(current.lastAttemptAt) ? current.lastAttemptAt : null
  // `now - lastAttemptAt < 0` is a clock that moved backwards (a manual date change, an NTP step).
  // Treating it as "too soon" is the safe direction: it delays one attempt rather than opening an
  // unthrottled loop, and the next launch with a sane clock proceeds.
  if (lastAttemptAt != null && now - lastAttemptAt < MIN_RETRY_INTERVAL_MS) {
    return { attempt: false, reason: 'backoff', record: current }
  }

  return { attempt: true, reason: 'eligible', record: current }
}

/**
 * Classify the backend's answer.
 *
 * ⚠️ A 200 IS NOT AUTOMATICALLY "DONE", AND READING IT THAT WAY IS THE BUG THIS SIGNATURE EXISTS TO
 * PREVENT. The backend answers 200 for outcomes that are explicitly NOT final — `error` (Apple was
 * unreachable or 5xx) and `pending` (another attempt held the lease, or the per-install cap was
 * reached) — and says so in `body.terminal`. An earlier version of this function latched on
 * `response.ok` alone, so a thirty-second Apple outage during a device's first launch would mark
 * the install `sent` forever, with the backend row stuck in `stale_unresolved` and nothing left to
 * retry it. That is precisely the failure the `terminal` flag was added to close, defeated by not
 * reading it. Same shape as ATL-962 on the dual-write outbox: a 200 whose body reports failure.
 *
 * A 200 whose body cannot be read is treated as NON-terminal. This endpoint is ours and always
 * answers JSON, so an unreadable answer is evidence of a problem, never evidence of completion —
 * and the retry it causes is bounded by MAX_ATTEMPTS like every other one.
 *
 * @param {{ok?: boolean, status?: number} | null | undefined} response
 * @param {unknown} [networkError]
 * @param {{terminal?: boolean, status?: string} | null} [body] the parsed 200 body, when there is one
 * @returns {'sent' | 'refused' | 'retry' | 'throttled'}
 */
export function classifyAttributionResponse(response, networkError, body) {
  if (networkError || !response) return 'retry'
  if (response.ok) return body && body.terminal === true ? 'sent' : 'retry'
  const status = Number(response.status)
  // 429 is OUR OWN outbound-call budget saying "later". It is not evidence about this install, so
  // it must not spend one of the install's attempts — eight throttled launches would otherwise
  // drive a perfectly good install to `abandoned`, which is terminal on the device.
  if (status === 429) return 'throttled'
  // ⚠️ ONLY THE 4xx THAT ARE STATEMENTS ABOUT THIS REQUEST ARE PERMANENT. Treating the whole 4xx
  // range as terminal was wrong for the ones that say nothing about the install: a Cloud Run 404
  // while a revision rolls, a proxy or WAF 403, a captive portal's 401. One of those landing on the
  // device's single eligible launch would abandon that install forever — attribution lost to
  // network furniture. 400 (malformed), 413 (too large) and 422 (unprocessable) are the ones that
  // will not change on a retry; everything else in the range is worth another launch. 408 is a
  // timeout, which may or may not have been processed, so it counts as an attempt.
  if (status === 400 || status === 413 || status === 422) return 'refused'
  return 'retry'
}

/**
 * Run one attribution attempt if policy allows it.
 *
 * Effects are injected so launch, offline, refusal and exhaustion are assertable without a device.
 *
 * @param {{
 *   installId: string,
 *   apiBaseUrl: string,
 *   appVersion?: string,
 *   platform?: string,
 *   now?: () => number,
 *   storage?: typeof AsyncStorage,
 *   fetchImpl?: typeof fetch,
 *   getToken?: () => Promise<string | null>,
 *   nativeAvailable?: () => boolean,
 *   onError?: (error: unknown) => void,
 * }} deps
 * @returns {Promise<{attempted: boolean, reason: string, status?: string}>}
 */
export async function runAppleAdsAttribution(deps) {
  const {
    installId,
    apiBaseUrl,
    appVersion,
    platform = Platform.OS,
    now = () => Date.now(),
    storage = AsyncStorage,
    fetchImpl = globalThis.fetch,
    getToken = getAttributionTokenAsync,
    nativeAvailable = isAvailable,
    onError,
  } = deps || {}

  try {
    if (!installId || !apiBaseUrl) {
      return { attempted: false, reason: 'not-configured' }
    }

    const record = await readRecord(storage)
    const decision = decideAttributionAttempt({
      record,
      installId,
      now: now(),
      platform,
      nativeAvailable: Boolean(nativeAvailable()),
    })

    if (!decision.attempt) {
      // Persist ONLY the decisions that changed the stored state (the two abandonment paths).
      // Writing on every ineligible launch would rewrite the same bytes on every foreground.
      if (decision.record && decision.record.status === 'abandoned' && record?.status !== 'abandoned') {
        await writeRecord(storage, decision.record)
      }
      return { attempted: false, reason: decision.reason, status: decision.record?.status }
    }

    const attemptedAt = now()
    // The token is minted HERE, immediately before the request — rule 1 in this file's header.
    // A token that cannot be minted is not an attempt: AdServices refusing is a device fact, not
    // evidence about this install's attribution, so it consumes an attempt but records no state
    // beyond the counter.
    let token = null
    try {
      token = await getToken()
    } catch (error) {
      onError?.(error)
      await writeRecord(storage, {
        ...decision.record,
        status: 'pending',
        attempts: (decision.record.attempts || 0) + 1,
        firstAttemptAt: decision.record.firstAttemptAt ?? attemptedAt,
        lastAttemptAt: attemptedAt,
        lastError: 'token-mint-failed',
      })
      return { attempted: true, reason: 'token-mint-failed', status: 'pending' }
    }

    if (!token) {
      // `null` means the native module vanished between the availability check and the call. Not
      // an attempt, not an error, and nothing to persist.
      return { attempted: false, reason: 'native-module-absent' }
    }

    let response = null
    let networkError = null
    let body = null
    try {
      const sent = await postWithTimeout(
        fetchImpl,
        `${apiBaseUrl}/attribution/apple-ads/token`,
        {
          installId,
          attributionToken: token,
          tokenMintedAt: new Date(attemptedAt).toISOString(),
          appVersion: appVersion ?? null,
        },
      )
      response = sent.response
      body = sent.body
    } catch (error) {
      networkError = error
    }

    const verdict = classifyAttributionResponse(response, networkError, body)

    // ⚠️ CAPTURED ONCE, AND PERSISTED ON EVERY BRANCH BELOW — not only the successful one.
    // The backend mints `installSecret` on an install's FIRST submission whatever that submission's
    // OUTCOME (the insert arm), and never re-issues it. So a first launch where Apple happened to be
    // unreachable returns the secret alongside a NON-terminal status; persisting it only on the
    // `sent` path would drop it, and the install could then never link — permanently, and silently,
    // because nothing downstream can tell a lost secret from an install that simply never linked.
    const grantedSecret = body?.installSecret ?? decision.record.installSecret ?? null

    // A throttle is our own backend declining to spend its outbound Apple budget. It says nothing
    // about this install, so it moves `lastAttemptAt` (which is what enforces the backoff) without
    // consuming one of the bounded attempts.
    if (verdict === 'throttled') {
      await writeRecord(storage, {
        ...decision.record,
        status: 'pending',
        firstAttemptAt: decision.record.firstAttemptAt ?? attemptedAt,
        lastAttemptAt: attemptedAt,
        lastError: 'throttled',
        installSecret: grantedSecret,
      })
      return { attempted: false, reason: 'throttled', status: 'pending' }
    }

    const nextRecord = {
      ...decision.record,
      attempts: (decision.record.attempts || 0) + 1,
      firstAttemptAt: decision.record.firstAttemptAt ?? attemptedAt,
      lastAttemptAt: attemptedAt,
      installSecret: grantedSecret,
    }

    if (verdict === 'sent') {
      await writeRecord(storage, { ...nextRecord, status: 'sent', sentAt: attemptedAt, lastError: null })
      return { attempted: true, reason: 'sent', status: 'sent' }
    }
    if (verdict === 'refused') {
      await writeRecord(storage, {
        ...nextRecord,
        status: 'refused',
        refusedAt: attemptedAt,
        lastError: `http-${response?.status}`,
      })
      // A permanent refusal is a defect on our side (a malformed install id, a rejected token
      // shape), not a normal outcome. It has to be visible or it is indistinguishable from an
      // install that simply had no attribution.
      onError?.(new Error(`Apple Ads attribution refused with HTTP ${response?.status}`))
      return { attempted: true, reason: 'refused', status: 'refused' }
    }

    await writeRecord(storage, {
      ...nextRecord,
      status: 'pending',
      lastError: networkError
        ? 'network'
        : response?.ok
          ? `not-terminal-${body?.status ?? 'unknown'}`
          : `http-${response?.status}`,
    })
    return { attempted: true, reason: 'retry', status: 'pending' }
  } catch (error) {
    // Rule 4. Nothing about marketing attribution may reach the launch path as a throw.
    onError?.(error)
    return { attempted: false, reason: 'error' }
  }
}

/**
 * @param {typeof fetch} fetchImpl
 * @param {string} url
 * @param {Record<string, unknown>} body
 * @returns {Promise<Response>}
 */
async function postWithTimeout(fetchImpl, url, body) {
  if (typeof fetchImpl !== 'function') throw new Error('fetch is unavailable')
  // AbortController exists in RN and on web. Where it does not, the request simply is not
  // time-boxed by us — still bounded by the platform's own socket timeout.
  const controller = typeof AbortController === 'function' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null
  try {
    const response = await fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...(controller ? { signal: controller.signal } : {}),
    })
    // ⚠️ THE BODY IS READ INSIDE THE ABORT WINDOW. `fetch` resolves as soon as the response HEADERS
    // arrive, so clearing the timer here and parsing afterwards left the body read with no timeout
    // at all — a server that sends headers and then stalls would hold the launch path open
    // indefinitely, and REQUEST_TIMEOUT_MS would bound nothing. The parsed body is returned with
    // the response because the caller needs `terminal` out of it.
    return { response, body: await readJson(response) }
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * Parse a JSON response body, or null when there is not one.
 *
 * Never throws: the caller treats `null` as "not terminal", which is the safe direction — an
 * unreadable answer must never be read as completion.
 *
 * @param {Response | null} response
 * @returns {Promise<object | null>}
 */
async function readJson(response) {
  try {
    if (!response || typeof response.json !== 'function') return null
    const parsed = await response.json()
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

/**
 * @param {typeof AsyncStorage} storage
 * @returns {Promise<object | null>}
 */
async function readRecord(storage) {
  try {
    const raw = await storage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (error) {
    // Unreadable state is treated as absent. The backend's own idempotency is what prevents that
    // from double-recording; a local parse failure must not permanently block attribution.
    console.warn('[growth] attribution state unreadable; starting over', error)
    return null
  }
}

/**
 * @param {typeof AsyncStorage} storage
 * @param {object} record
 * @returns {Promise<void>}
 */
async function writeRecord(storage, record) {
  try {
    await storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify({ v: 1, ...record }))
  } catch (error) {
    console.warn('[growth] attribution state could not be persisted', error)
  }
}

/** Test seam: read the persisted record. */
export function readAttributionRecordForTests(storage = AsyncStorage) {
  return readRecord(storage)
}

/**
 * The proof-of-possession secret for THIS install, or null when there is not one.
 *
 * ⚠️ THE INSTALL ID IS CHECKED, for the same reason `decideAttributionAttempt` checks it: a record
 * belonging to a DIFFERENT install is not this install's record. After an id regeneration (a
 * corrupt stored value, an ephemeral id from a storage failure) returning the old secret would post
 * the NEW id with the OLD secret — a 403 and a Sentry capture on every launch, until a fresh
 * submission happens to overwrite it.
 *
 * Null is also the normal case for any install that never submitted an Apple token — Android, web,
 * and an iOS binary without the native module. Those installs simply do not link; they contribute
 * nothing to the attributed views, so nothing is lost.
 *
 * @param {string | null | undefined} installId the id the caller is about to link
 * @param {typeof AsyncStorage} [storage]
 * @returns {Promise<string | null>}
 */
export async function readInstallSecret(installId, storage = AsyncStorage) {
  const record = await readRecord(storage)
  if (!record || record.installId !== installId) return null
  return typeof record.installSecret === 'string' ? record.installSecret : null
}
